import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import {
  FinancialRecordDialog,
  type FinancialDialogState,
} from "./-components/financials/financial-record-dialog";
import { IncomeCard } from "./-components/financials/income-card";
import { NetWorthCard } from "./-components/financials/net-worth-card";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Skeleton } from "@/components/ui/skeleton";

const fail = (error: Error) => toast.error(errorWords(error));

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/financials")({
  component: FinancialsTab,
});

type DialogState = { kind: "income" | "asset" | "liability"; id?: string } | null;

function FinancialsTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canWrite = useCan("resident.financials:update", resident.facilityId);
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [removing, setRemoving] = useState<{
    title: string;
    description: string;
    run: () => void;
  } | null>(null);
  const input = { residentId };
  const summary = useQuery(orpc.care.residents.financials.summary.queryOptions({ input }));
  const income = useQuery(orpc.care.residents.financials.income.list.queryOptions({ input }));
  const assets = useQuery(orpc.care.residents.financials.assets.list.queryOptions({ input }));
  const liabilities = useQuery(
    orpc.care.residents.financials.liabilities.list.queryOptions({ input }),
  );
  const done = (message: string) => async () => {
    toast.success(message);
    setDialog(null);
    await queryClient.invalidateQueries({ queryKey: orpc.care.residents.financials.key() });
  };
  const m = {
    incomeCreate: useMutation(
      orpc.care.residents.financials.income.create.mutationOptions({
        onSuccess: done("Income added"),
        onError: fail,
      }),
    ),
    incomeUpdate: useMutation(
      orpc.care.residents.financials.income.update.mutationOptions({
        onSuccess: done("Income updated"),
        onError: fail,
      }),
    ),
    incomeRemove: useMutation(
      orpc.care.residents.financials.income.remove.mutationOptions({
        onSuccess: done("Income removed"),
        onError: fail,
      }),
    ),
    assetCreate: useMutation(
      orpc.care.residents.financials.assets.create.mutationOptions({
        onSuccess: done("Asset added"),
        onError: fail,
      }),
    ),
    assetUpdate: useMutation(
      orpc.care.residents.financials.assets.update.mutationOptions({
        onSuccess: done("Asset updated"),
        onError: fail,
      }),
    ),
    assetRemove: useMutation(
      orpc.care.residents.financials.assets.remove.mutationOptions({
        onSuccess: done("Asset removed"),
        onError: fail,
      }),
    ),
    liabilityCreate: useMutation(
      orpc.care.residents.financials.liabilities.create.mutationOptions({
        onSuccess: done("Liability added"),
        onError: fail,
      }),
    ),
    liabilityUpdate: useMutation(
      orpc.care.residents.financials.liabilities.update.mutationOptions({
        onSuccess: done("Liability updated"),
        onError: fail,
      }),
    ),
    liabilityRemove: useMutation(
      orpc.care.residents.financials.liabilities.remove.mutationOptions({
        onSuccess: done("Liability removed"),
        onError: fail,
      }),
    ),
  };
  const pending = Object.values(m).some((x) => x.isPending);
  const open: FinancialDialogState =
    dialog === null
      ? null
      : dialog.kind === "income"
        ? { kind: "income", row: income.data?.find((r) => r.id === dialog.id) }
        : dialog.kind === "asset"
          ? { kind: "asset", row: assets.data?.find((r) => r.id === dialog.id) }
          : { kind: "liability", row: liabilities.data?.find((r) => r.id === dialog.id) };
  const incomeRows = income.data ?? [];
  const assetRows = assets.data ?? [];
  const liabilityRows = liabilities.data ?? [];
  /* Money is never shown as zero or "none" because a read did not finish. */
  const reads = [summary, income, assets, liabilities];
  const failed = reads.find((read) => read.isError);
  if (reads.some((read) => read.isPending)) {
    return (
      <div className="space-y-6" aria-busy="true">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (failed) {
    return (
      <ReadFailed
        message="Could not load income and assets."
        error={failed.error}
        onRetry={() => reads.forEach((read) => void read.refetch())}
        retrying={reads.some((read) => read.isFetching)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="sr-only">Income and assets</h2>
      <IncomeCard
        rows={incomeRows}
        monthlyIncomeCents={summary.data?.monthlyIncomeCents}
        canWrite={canWrite}
        onAdd={() => setDialog({ kind: "income" })}
        onEdit={(row) => setDialog({ kind: "income", id: row.id })}
        onDelete={(row) =>
          setRemoving({
            title: `Delete ${row.sourceName}?`,
            description:
              "This income source comes off the resident's record. This cannot be undone.",
            run: () => m.incomeRemove.mutate({ incomeSourceId: row.id }),
          })
        }
      />
      <NetWorthCard
        assets={assetRows}
        liabilities={liabilityRows}
        totals={summary.data}
        canWrite={canWrite}
        onAddAsset={() => setDialog({ kind: "asset" })}
        onAddLiability={() => setDialog({ kind: "liability" })}
        onEditAsset={(row) => setDialog({ kind: "asset", id: row.id })}
        onEditLiability={(row) => setDialog({ kind: "liability", id: row.id })}
        onDeleteAsset={(row) =>
          setRemoving({
            title: `Delete ${row.assetName}?`,
            description: "This asset comes off the resident's record. This cannot be undone.",
            run: () => m.assetRemove.mutate({ assetId: row.id }),
          })
        }
        onDeleteLiability={(row) =>
          setRemoving({
            title: `Delete ${row.creditorName}?`,
            description: "This liability comes off the resident's record. This cannot be undone.",
            run: () => m.liabilityRemove.mutate({ liabilityId: row.id }),
          })
        }
      />
      {removing ? (
        <RemoveConfirm
          title={removing.title}
          description={removing.description}
          onConfirm={removing.run}
          onClose={() => setRemoving(null)}
        />
      ) : null}
      <FinancialRecordDialog
        dialog={open}
        pending={pending}
        contentClassName="max-w-2xl"
        onClose={() => setDialog(null)}
        onSaveIncome={(row, values) =>
          row
            ? m.incomeUpdate.mutate({ incomeSourceId: row.id, ...values })
            : m.incomeCreate.mutate({ residentId, ...values })
        }
        onSaveAsset={(row, values) =>
          row
            ? m.assetUpdate.mutate({ assetId: row.id, ...values })
            : m.assetCreate.mutate({ residentId, ...values })
        }
        onSaveLiability={(row, values) =>
          row
            ? m.liabilityUpdate.mutate({ liabilityId: row.id, ...values })
            : m.liabilityCreate.mutate({ residentId, ...values })
        }
      />
    </div>
  );
}
